/**
 * BrandBannerFallbacksManager — the pool of generic banner images RN shows on a
 * brand detail screen when that brand has no bespoke `heroImage`.
 *
 * State lives in RTDB at `configuration/brandBannerFallbacks` (see
 * `services/brandBannerFallbacks`); the bytes live in Storage under
 * `admin/brandBannerFallbacks/`. Everything here is a direct client write —
 * no callable — which is fine for a super-admin-only panel and keeps
 * multi-megabyte uploads off the functions path.
 *
 * The list is realtime, so a second admin's upload or toggle lands here without
 * a refresh, and the modal editor sees the same live row.
 */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common/Button';
import { BannerPreview } from './BannerPreview';
import { FallbackEditorModal } from './FallbackEditorModal';
import {
  BRAND_BANNER_FALLBACK_ACCEPT,
  BRAND_BANNER_FALLBACK_MAX_BYTES,
  uploadBrandBannerFallback,
} from '../../services/firebase';
import {
  addBrandBannerFallback,
  newBrandBannerFallbackId,
  setBrandBannerFallbacksEnabled,
  subscribeToBrandBannerFallbacks,
  updateBrandBannerFallback,
  type BrandBannerFallbackImage,
} from '../../services/brandBannerFallbacks';

/** One entry in the in-flight upload queue shown above the grid. */
interface UploadRow {
  name: string;
  percent: number;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  error?: string;
  /**
   * Set only when the file was downscaled before upload. The queue is the one
   * place the admin sees the dimensions they actually picked — the tile
   * previews read theirs off the stored object, which is the resized one.
   */
  resizedFrom?: string;
}

export function BrandBannerFallbacksManager() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [images, setImages] = useState<BrandBannerFallbackImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // True while a master-toggle write is in flight, so the live subscription
  // doesn't bounce the switch back to the old value before RTDB echoes ours.
  const enabledSaving = useRef(false);

  useEffect(() => {
    const unsubscribe = subscribeToBrandBannerFallbacks(
      (config) => {
        if (!enabledSaving.current) setEnabled(config.enabled);
        setImages(config.images);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        setLoadError(err.message);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  // The editor works off the LIVE row rather than a snapshot taken at open
  // time, so a replace or a toggle from elsewhere is reflected immediately.
  // A row deleted while open resolves to undefined and closes the modal.
  const editingImage = editingId
    ? images.find((image) => image.id === editingId)
    : undefined;
  useEffect(() => {
    if (editingId && !editingImage) setEditingId(null);
  }, [editingId, editingImage]);

  const enabledCount = images.filter((image) => image.enabled).length;

  const handleToggleFeature = async (next: boolean) => {
    enabledSaving.current = true;
    setEnabled(next);
    setSavingEnabled(true);
    try {
      await setBrandBannerFallbacksEnabled(next);
      toast.success(next ? 'Fallback banners on' : 'Fallback banners off');
    } catch (err) {
      console.error('Failed to toggle brand banner fallbacks:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save');
      setEnabled(!next);
    } finally {
      enabledSaving.current = false;
      setSavingEnabled(false);
    }
  };

  /**
   * Upload a batch sequentially, not in parallel.
   *
   * Same reasoning as the roadmap attachments panel: the progress readout is
   * per-file, and several large images going up at once mostly starve each
   * other's bandwidth. Unlike roadmap, one failure does NOT stop the batch —
   * a single unsupported file in a drop of twelve shouldn't discard the rest;
   * failures are marked in the queue and summarised at the end.
   */
  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    setUploads(files.map((file) => ({ name: file.name, percent: 0, status: 'pending' })));

    let failures = 0;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      setUploads((rows) =>
        rows.map((row, index) => (index === i ? { ...row, status: 'uploading' } : row)),
      );
      try {
        // Mint the RTDB key first so the Storage object can be named after it —
        // the two halves stay reconcilable by id alone. A failed upload leaves
        // no row at all, since nothing is written until the bytes are up.
        const id = newBrandBannerFallbackId();
        const { storagePath, downloadUrl, resized, originalWidth, originalHeight } =
          await uploadBrandBannerFallback(id, file, {
            onProgress: (percent) =>
              setUploads((rows) =>
                rows.map((row, index) => (index === i ? { ...row, percent } : row)),
              ),
          });
        await addBrandBannerFallback({
          id,
          url: downloadUrl,
          storagePath,
          filename: file.name,
          enabled: true,
          addedAt: Date.now(),
          addedBy: user?.uid ?? '',
        });
        const resizedFrom =
          resized && originalWidth > 0 && originalHeight > 0
            ? `Resized from ${originalWidth} × ${originalHeight}`
            : undefined;
        setUploads((rows) =>
          rows.map((row, index) =>
            index === i ? { ...row, percent: 100, status: 'done', resizedFrom } : row,
          ),
        );
      } catch (err) {
        console.error(`Failed to upload banner fallback ${file.name}:`, err);
        failures += 1;
        setUploads((rows) =>
          rows.map((row, index) =>
            index === i
              ? {
                  ...row,
                  status: 'failed',
                  error: err instanceof Error ? err.message : 'Upload failed',
                }
              : row,
          ),
        );
      }
    }

    setUploading(false);
    if (failures === 0) {
      toast.success(`Uploaded ${files.length} image${files.length === 1 ? '' : 's'}`);
      setUploads([]);
    } else {
      toast.error(`${failures} of ${files.length} upload(s) failed`);
      // Failed rows stay on screen with their reason; the admin clears them.
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    const dropped = Array.from(e.dataTransfer.files ?? []);
    // A drop bypasses the input's `accept`, so filter here too. The service
    // layer re-checks; this only avoids a toast per stray file.
    const validImages = dropped.filter((file) => file.type.startsWith('image/'));
    if (validImages.length === 0) {
      toast.error('Only images can be uploaded');
      return;
    }
    if (validImages.length < dropped.length) {
      toast.error(`Skipped ${dropped.length - validImages.length} non-image file(s)`);
    }
    await handleFiles(validImages);
  };

  const handleToggleImage = async (image: BrandBannerFallbackImage) => {
    setTogglingId(image.id);
    try {
      await updateBrandBannerFallback(image.id, { enabled: !image.enabled });
    } catch (err) {
      console.error('Failed to toggle banner fallback:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setTogglingId(null);
    }
  };

  const maxMb = Math.round(BRAND_BANNER_FALLBACK_MAX_BYTES / (1024 * 1024));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Brand Banner Fallbacks</h2>
        <p className="mt-1 text-sm text-gray-600">
          Generic banner images the app shows on a brand page when that brand has no
          hero image of its own. Each brand gets one image from this pool, picked
          consistently — the same brand shows the same banner to everyone.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => void handleToggleFeature(e.target.checked)}
            disabled={loading || savingEnabled}
            className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">
              Fallback banners enabled
            </span>
            <span className="block text-xs text-gray-500">
              Off means brands without a hero image fall back to the app’s existing
              behaviour. Turning this off keeps the pool — nothing is deleted.
            </span>
          </span>
        </label>
        {enabled && enabledCount === 0 && !loading ? (
          <p className="mt-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Enabled, but no images are active — the app has nothing to fall back to.
          </p>
        ) : null}
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Pool</h3>
          {!loading && !loadError ? (
            <span className="text-xs text-gray-500">
              {enabledCount} active
              {images.length !== enabledCount ? ` · ${images.length - enabledCount} disabled` : ''}
            </span>
          ) : null}
          <div className="ml-auto">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Upload images
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={BRAND_BANNER_FALLBACK_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) void handleFiles(files);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`rounded-lg border-2 border-dashed p-4 transition-colors ${
            dragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50'
          }`}
        >
          <p className="text-center text-sm text-gray-500">
            Drop images here to add them to the pool — up to {maxMb} MB each. Landscape,
            at least 686px wide, works best.
          </p>
        </div>

        {uploads.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            {uploads.map((row, index) => (
              <div key={`${row.name}-${index}`} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="truncate text-gray-700">{row.name}</span>
                  {row.resizedFrom ? (
                    <span className="shrink-0 text-[11px] text-gray-400">
                      {row.resizedFrom}
                    </span>
                  ) : null}
                  <span
                    className={
                      row.status === 'failed'
                        ? 'ml-auto shrink-0 text-red-600'
                        : 'ml-auto shrink-0 text-gray-500'
                    }
                  >
                    {row.status === 'failed'
                      ? (row.error ?? 'Failed')
                      : row.status === 'done'
                        ? 'Done'
                        : row.status === 'pending'
                          ? 'Waiting'
                          : `${row.percent}%`}
                  </span>
                </div>
                <div className="mt-0.5 h-1 overflow-hidden rounded bg-gray-200">
                  <div
                    className={`h-full transition-all ${
                      row.status === 'failed' ? 'bg-red-400' : 'bg-brand-500'
                    }`}
                    style={{ width: `${row.status === 'failed' ? 100 : row.percent}%` }}
                  />
                </div>
              </div>
            ))}
            {!uploading && uploads.some((row) => row.status === 'failed') ? (
              <Button variant="ghost" size="sm" onClick={() => setUploads([])}>
                Clear
              </Button>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <p className="mt-4 text-sm text-gray-500">Loading…</p>
        ) : loadError ? (
          <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            Could not load the pool: {loadError}
          </p>
        ) : images.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">
            No images yet. Upload a few landscape shots to get started.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {images.map((image) => (
              <div
                key={image.id}
                className={`rounded-lg border p-3 transition-opacity ${
                  image.enabled ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                <BannerPreview
                  url={image.url}
                  alt={image.filename || image.id}
                  overlay={
                    image.enabled ? null : (
                      <span className="rounded bg-gray-900/75 px-1.5 py-0.5 text-[11px] font-medium text-white">
                        Disabled
                      </span>
                    )
                  }
                />
                {image.label ? (
                  <p className="mt-2 truncate text-sm font-medium text-gray-900">
                    {image.label}
                  </p>
                ) : null}
                <p className="mt-1 truncate text-xs text-gray-500" title={image.filename}>
                  {image.filename || image.id}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant={image.enabled ? 'ghost' : 'secondary'}
                    size="sm"
                    onClick={() => void handleToggleImage(image)}
                    disabled={togglingId === image.id}
                  >
                    {image.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setEditingId(image.id)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingImage ? (
        <FallbackEditorModal image={editingImage} onClose={() => setEditingId(null)} />
      ) : null}
    </div>
  );
}
